import { useFormHook, useSetError } from '@Hooks/index';
import { useNavigationManager } from '@Router/useNavigationManager';
import { passwordScheme } from '@Components/inputs/PasswordInput';
import { OnboardingFormFields } from '@Types/index';
import { z } from 'zod';
import { useFormState } from 'react-hook-form';
import { useCompleteOnboarding, useSetAuthorizedUserPassword } from '../api/setPassword.api';
import { useAuthorizedUserConsent } from '../api/useAuthorizedUserConsent';

export interface SetPasswordFormType {
  [OnboardingFormFields.PASSWORD]: string;
}

const setPasswordFormScheme = z.object({
  ...passwordScheme.shape,
});

export const useSetPasswordLogic = () => {
  const { navigateToNextPage } = useNavigationManager();
  const { mutateAsync: setPassword, isPending: isSettingPassword } = useSetAuthorizedUserPassword();
  const { mutateAsync: completeOnboarding, isPending: isCompletingOnboarding } =
    useCompleteOnboarding();
  const { mutateAsync: sendConsent, isPending: isSendingConsent } = useAuthorizedUserConsent();
  const defaultValues: SetPasswordFormType = {
    [OnboardingFormFields.PASSWORD]: '',
  };

  const formMethods = useFormHook({
    formSchema: setPasswordFormScheme,
    formDefaultValues: defaultValues,
    schemaTitle: 'set-password-form',
  });

  const { isValid } = useFormState({ control: formMethods.control });

  const handleSubmit = async (data: SetPasswordFormType) => {
    try {
      const res = await setPassword({ password: data[OnboardingFormFields.PASSWORD] });
      await sendConsent(res.token);
      await completeOnboarding();
      navigateToNextPage();
    } catch (error) {
      console.error('Error setting password:', error);
    }
  };
  useSetError({ formMethods });

  return {
    formMethods,
    handleSubmit,
    isSettingPassword,
    isValid,
    isCompletingOnboarding,
    isSendingConsent,
  };
};
